import React, { useState, useRef, useEffect } from "react";
import { beepAndVibrate } from "../utils/feedback";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Camera,
  Upload,
  RotateCcw,
  CheckCircle,
  Square,
} from "lucide-react";
import geminiService from "../services/gemini";
import { apiService } from "../services/apiService";
import { compressImage } from "../utils/imageCompression";
import { resolveNamaIbu } from "../utils/motherName";
import toast from "react-hot-toast";

const TTDCapsuleScreen = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [aiResult, setAiResult] = useState(null);
  const [manualCount, setManualCount] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState(null);
  const [barcodeType, setBarcodeType] = useState(null);
  const [productName, setProductName] = useState(null);
  const [nameMother, setNameMother] = useState(null);
  const [loadingMother, setLoadingMother] = useState(false);
  const [manualNameMother, setManualNameMother] = useState("");
  const [confirmedNameMother, setConfirmedNameMother] = useState("");
  const [idIbu, setIdIbu] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const motherInfo = location.state?.motherInfo;

  // Get barcode data from navigation state
  useEffect(() => {
    if (location.state) {
      setScannedBarcode(location.state.barcode);
      setBarcodeType(location.state.barcodeType);
      setProductName(location.state.productName);
      if (location.state.nameMother) {
        setConfirmedNameMother(location.state.nameMother);
      }
      if (location.state.idIbu) {
        setIdIbu(location.state.idIbu);
      }
    }
  }, [location.state]);

  // Fetch name_mother when barcode is available
  useEffect(() => {
    const fetchNameMother = async () => {
      if (scannedBarcode) {
        setLoadingMother(true);
        try {
          const scanData = await apiService.scanBarcode(scannedBarcode);
          if (scanData.name_mother) {
            setNameMother(scanData.name_mother);
          }
          if (scanData.id_ibu || scanData.respondent_id) {
            setIdIbu(scanData.id_ibu || scanData.respondent_id);
          }
        } catch (error) {
          console.error("Error fetching name_mother:", error);
          // Don't show error to user, just log it
        } finally {
          setLoadingMother(false);
        }
      }
    };

    fetchNameMother();
  }, [scannedBarcode]);

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        setSelectedImage(file);
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        setAiResult(null);
        setManualCount("");
      } else {
        toast.error("Please select an image file");
      }
    }
  };

  const handleRetake = () => {
    setSelectedImage(null);
    setPreviewUrl(null);
    setAiResult(null);
    setManualCount("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const analyzeImage = async () => {
    if (!selectedImage) {
      toast.error("Please select an image first");
      return;
    }

    setLoading(true);
    try {
      // Standardize & compress image before sending to Gemini
      const compressedImage = await compressImage(selectedImage, {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.85,
      });
      const arrayBuffer = await compressedImage.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const result = await geminiService.analyzeTTDCapsules(uint8Array);

      beepAndVibrate();

      toast.success("Analysis Completed!");
      setAiResult(result);
      setManualCount(result.capsuleCount.toString());
    } catch (error) {
      console.error("Error analyzing image:", error);
      toast.error("Failed to analyze image. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedImage || !manualCount) {
      toast.error("Please provide an image and capsule count");
      return;
    }

    if (!scannedBarcode) {
      toast.error("Barcode ID is required. Please scan barcode first.");
      return;
    }

    const namaIbu = resolveNamaIbu({
      nameMother,
      confirmedNameMother,
      manualNameMother,
    });
    if (!namaIbu) {
      toast.error("Nama ibu diperlukan. Silakan masukkan nama ibu.");
      return;
    }

    setSubmitting(true);
    try {
      const jumlahPills = parseInt(manualCount);

      if (isNaN(jumlahPills) || jumlahPills <= 0) {
        toast.error("Please enter a valid capsule count");
        setSubmitting(false);
        return;
      }

      const captureId = aiResult?.capture_id || null;
      const imageUrl = aiResult?.image_url || aiResult?.gcs_uri || null;

      // Submit to new BigQuery counting table using /submit-counting endpoint with capture_id, image_url, nama_ibu, and id_ibu
      const result = await apiService.submitCounting({
        barcode_id: scannedBarcode,
        jumlah_pills: jumlahPills,
        detection_type: "ttd_capsule",
        nama_ibu: namaIbu,
        id_ibu: idIbu || null,
        capture_id: captureId,
        image_url: imageUrl,
      });

      toast.success("TTD capsule count submitted successfully!");

      const imageAttachment = previewUrl;

      // Navigate to results with submitted data
      navigate("/results", {
        state: {
          record: {
            barcode_id: scannedBarcode,
            jumlah_pills: jumlahPills,
            detection_type: "ttd_capsule",
            jenis_counting: "TTD Capsule",
            nama_ibu: namaIbu,
            id_ibu: idIbu || result?.id_ibu || null,
            capture_id: captureId,
            image_url: result?.image_url || imageUrl,
            image: imageAttachment,
            ...result,
          },
          motherInfo,
          aiResult,
          capture_id: captureId,
          image: imageAttachment,
        },
      });
    } catch (error) {
      console.error("Error submitting record:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to submit record";
      toast.error(`Failed to submit record: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={() => navigate("/")}
              className="mr-4 p-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              TTD Capsule Detection
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mother Info */}
        {motherInfo && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              Mother Information
            </h3>
            <p className="text-blue-900 dark:text-blue-100">
              {motherInfo.mother_name}
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Barcode: {motherInfo.barcode_id}
            </p>
          </div>
        )}

        {/* Scanned Barcode Information */}
        {scannedBarcode && (
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6">
            <div className="flex items-center mb-3">
              <CheckCircle className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" />
              <h3 className="text-sm font-medium text-purple-800 dark:text-purple-200">
                Scanned Barcode Information
              </h3>
            </div>
            <div className="space-y-2">
              <p className="text-purple-900 dark:text-purple-100 font-medium">
                {productName} - {barcodeType}
              </p>
              <p className="text-sm text-purple-700 dark:text-purple-300 font-mono">
                Barcode ID: {scannedBarcode}
              </p>
              {idIbu && (
                <p className="text-xs font-mono text-purple-700 dark:text-purple-300">
                  ID Ibu: <span className="font-semibold">{idIbu}</span>
                </p>
              )}
              {loadingMother ? (
                <p className="text-sm text-purple-600 dark:text-purple-400 italic">
                  Memuat nama ibu...
                </p>
              ) : nameMother ? (
                <p className="text-sm text-purple-700 dark:text-purple-300">
                  Nama Ibu: <span className="font-semibold">{nameMother}</span>
                </p>
              ) : confirmedNameMother ? (
                <div className="space-y-2">
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    Nama Ibu:{" "}
                    <span className="font-semibold">{confirmedNameMother}</span>
                  </p>
                  <button
                    onClick={() => {
                      setConfirmedNameMother("");
                      setManualNameMother(confirmedNameMother);
                    }}
                    className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 underline"
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-purple-600 dark:text-purple-400 italic mb-2">
                    Data tidak ditemukan. Silakan input manual:
                  </p>
                  <input
                    type="text"
                    value={manualNameMother}
                    onChange={(e) => setManualNameMother(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && manualNameMother.trim()) {
                        setConfirmedNameMother(manualNameMother.trim());
                        toast.success("Nama ibu tersimpan");
                      }
                    }}
                    placeholder="Masukkan nama ibu"
                    className="w-full px-3 py-2 border border-purple-300 dark:border-purple-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  {manualNameMother && (
                    <button
                      onClick={() => {
                        if (manualNameMother.trim()) {
                          setConfirmedNameMother(manualNameMother.trim());
                          toast.success("Nama ibu tersimpan");
                        }
                      }}
                      className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                    >
                      Simpan Nama Ibu
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Square className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-3 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">
                TTD Capsule Detection Instructions
              </h3>
              <ul className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1">
                <li>
                  • Position the strip packaging clearly in the camera view
                </li>
                <li>• Ensure good lighting for clear visibility</li>
                <li>• Make sure all capsules are visible in the strip</li>
                <li>• The AI will count capsules wrapped in foil</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Image Capture Section */}
        {!previewUrl && (
          <div className="text-center">
            <div className="mb-8">
              <div className="mx-auto h-24 w-24 bg-indigo-100 dark:bg-indigo-900 rounded-full flex items-center justify-center mb-4">
                <Camera className="h-12 w-12 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Capture Strip Packaging
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Take a clear photo of the TTD capsule strip packaging
              </p>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => cameraInputRef.current?.click()}
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <Camera className="h-5 w-5 inline mr-2" />
                Take Photo
              </button>

              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                <Upload className="h-5 w-5 inline mr-2" />
                Choose from Gallery
              </button>
            </div>

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*;capture=camera"
              onChange={handleImageSelect}
              className="hidden"
              capture="environment"
            />

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
          </div>
        )}

        {/* Image Preview and Analysis */}
        {previewUrl && (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Review Image
              </h2>
              <p className="text-gray-600 dark:text-gray-300">
                Check the image and proceed with AI analysis
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
              <img
                src={previewUrl}
                alt="Strip packaging"
                className="w-full rounded-lg"
                style={{ maxHeight: "400px", objectFit: "contain" }}
              />
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleRetake}
                className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                <RotateCcw className="h-4 w-4 inline mr-2" />
                Retake
              </button>

              <button
                onClick={analyzeImage}
                disabled={loading}
                className="flex-1 bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Analyzing...
                  </div>
                ) : (
                  "Analyze"
                )}
              </button>
            </div>

            {/* AI Results */}
            {aiResult && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400 mr-2" />
                  <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                    Analysis Complete
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-green-700 dark:text-green-300">
                      Detected Capsules
                    </label>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                      {aiResult.capsuleCount}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 dark:text-green-300">
                      Confidence
                    </label>
                    <p className="text-lg font-semibold text-green-900 dark:text-green-100">
                      {Math.round(aiResult.confidence * 100)}%
                    </p>
                  </div>
                </div>

                {aiResult.description && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-200 dark:border-green-700">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>Analysis:</strong> {aiResult.description}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Manual Count Input */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Final Capsule Count
              </h3>
              <div className="space-y-4">
                {/* Barcode ID Field (Read-only) */}
                {scannedBarcode && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Barcode ID (Scanned)
                    </label>
                    <input
                      type="text"
                      value={scannedBarcode}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      This field is automatically filled from the scanned
                      barcode
                    </p>
                  </div>
                )}

                <div>
                  <label
                    htmlFor="capsuleCount"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Number of Capsules
                  </label>
                  <input
                    type="number"
                    id="capsuleCount"
                    value={manualCount}
                    onChange={(e) => setManualCount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    placeholder="Enter capsule count"
                    min="0"
                  />
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!manualCount || submitting}
                  className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    "Submit Count"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TTDCapsuleScreen;
